import { useMemo, useRef, useState } from "react";
import { cn } from "../../utils/cn";
import { formatDate, formatMonthYear } from "../../utils/format";
import { useClickOutside } from "../../hooks/useClickOutside";

export interface DateRange {
  start: string | null;
  end: string | null;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  compact?: boolean;
  placeholder?: string;
  align?: "left" | "right" | "auto";
}

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const PRESETS: { label: string; build: () => DateRange }[] = [
  { label: "Today", build: () => { const d = new Date(); return { start: d.toISOString(), end: d.toISOString() }; } },
  {
    label: "This week",
    build: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      return { start: start.toISOString(), end: now.toISOString() };
    },
  },
  {
    label: "This month",
    build: () => {
      const now = new Date();
      return { start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), end: now.toISOString() };
    },
  },
  {
    label: "Last month",
    build: () => {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: first.toISOString(), end: last.toISOString() };
    },
  },
  {
    label: "This year",
    build: () => {
      const now = new Date();
      return { start: new Date(now.getFullYear(), 0, 1).toISOString(), end: now.toISOString() };
    },
  },
];

function buildCells(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(year, month, d));
  return cells;
}

function DayButton({
  day,
  inRange,
  isStart,
  isEnd,
  onClick,
}: {
  day: Date;
  inRange: boolean;
  isStart: boolean;
  isEnd: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative mx-auto flex h-7 w-7 cursor-pointer items-center justify-center text-xs hover:bg-background-200",
        inRange && "bg-primary-100 text-primary-900",
        isStart && "rounded-l-md bg-primary-500 font-semibold text-white hover:bg-primary-600",
        isEnd && "rounded-r-md bg-primary-500 font-semibold text-white hover:bg-primary-600",
        !inRange && "text-foreground-700"
      )}
    >
      {day.getDate()}
    </button>
  );
}

export function DateRangePicker({
  value,
  onChange,
  compact,
  placeholder = "Select date range",
  align = "auto",
}: DateRangePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [dropdownAlign, setDropdownAlign] = useState<"left" | "right">("left");
  const anchor = value.start ? new Date(value.start) : new Date();
  const [viewYear, setViewYear] = useState(anchor.getFullYear());
  const [viewMonth, setViewMonth] = useState(anchor.getMonth());

  useClickOutside(rootRef, () => setOpen(false), open);

  const updateAlignment = () => {
    if (!rootRef.current) return;
    if (align === "left" || align === "right") {
      setDropdownAlign(align);
      return;
    }
    const rect = rootRef.current.getBoundingClientRect();
    const spaceOnRight = window.innerWidth - rect.left;
    const spaceOnLeft = rect.right;
    const popupWidth = window.innerWidth >= 640 ? 560 : 320;
    // If not enough room on the right for popup, but enough on the left, align to the right
    if (spaceOnRight < popupWidth && spaceOnLeft >= popupWidth) {
      setDropdownAlign("right");
    } else {
      setDropdownAlign("left");
    }
  };

  useEffect(() => {
    if (!open) return;
    updateAlignment();
    const handleResize = () => updateAlignment();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, align]);

  const leftCells = useMemo(() => buildCells(viewYear, viewMonth), [viewYear, viewMonth]);
  const rightCells = useMemo(() => buildCells(viewMonth === 11 ? viewYear + 1 : viewYear, viewMonth === 11 ? 0 : viewMonth + 1), [viewYear, viewMonth]);

  const startDate = value.start ? new Date(value.start) : null;
  const endDate = value.end ? new Date(value.end) : null;
  const startMs = startDate ? startDate.getTime() : null;
  const endMs = endDate ? endDate.getTime() : null;

  const dayProps = (day: Date) => {
    const ms = day.getTime();
    const inRange = startMs !== null && endMs !== null && ms >= startMs && ms <= endMs;
    const isStart = startMs !== null && ms === startMs;
    const isEnd = endMs !== null && ms === endMs;
    return { inRange, isStart, isEnd };
  };

  const selectDay = (day: Date) => {
    if (!startDate || (startDate && endDate)) {
      onChange({ start: day.toISOString(), end: null });
    } else {
      const first = startDate.getTime();
      const ms = day.getTime();
      onChange(ms < first ? { start: day.toISOString(), end: startDate.toISOString() } : { start: startDate.toISOString(), end: day.toISOString() });
    }
  };

  const rangeLabel = value.start && value.end ? `${formatDate(value.start)} – ${formatDate(value.end)}` : value.start ? `${formatDate(value.start)} – …` : placeholder;

  const moveLeft = (delta: number) => {
    const next = (viewMonth + delta + 12) % 12;
    setViewMonth(next);
    if (next === 0 && delta === -1) setViewYear((y) => y - 1);
    else if (next === 11 && delta === 1) setViewYear((y) => y + 1);
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => {
          if (!open) updateAlignment();
          setOpen((v) => !v);
        }}
        className={cn(
          "flex h-9 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400/40",
          compact && "h-8 text-xs"
        )}
      >
        <i className="ri-calendar-line text-base text-foreground-400" />
        <span className={cn("tnum", !value.start && "text-foreground-400")}>{rangeLabel}</span>
        <i className="ri-arrow-down-s-line text-base text-foreground-400" />
      </button>

      {open ? (
        <div
          className={cn(
            "absolute z-50 mt-1 w-[calc(100vw-2rem)] max-w-[320px] sm:w-[560px] sm:max-w-none rounded-md border border-background-200 bg-background-50 p-3 shadow-xl shadow-foreground-950/10",
            dropdownAlign === "right" ? "right-0 left-auto" : "left-0 right-auto"
          )}
        >
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => onChange(p.build())}
                className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900 hover:bg-secondary-200"
              >
                {p.label}
              </button>
            ))}
            {value.start ? (
              <button type="button" onClick={() => onChange({ start: null, end: null })} className="ml-auto cursor-pointer text-xs text-danger-600 hover:underline">
                Clear
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center justify-between">
                <button type="button" onClick={() => moveLeft(-1)} aria-label="Previous" className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md hover:bg-background-100">
                  <i className="ri-arrow-left-s-line" />
                </button>
                <span className="text-sm font-semibold text-foreground-800">{formatMonthYear(new Date(viewYear, viewMonth, 1))}</span>
                <button type="button" onClick={() => moveLeft(1)} aria-label="Next" className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md hover:bg-background-100 sm:hidden">
                  <i className="ri-arrow-right-s-line" />
                </button>
                <span className="hidden w-7 sm:inline-block" />
              </div>
              <MonthGrid cells={leftCells} dayProps={dayProps} onSelect={selectDay} />
            </div>
            <div className="hidden sm:block">
              <div className="mb-1 flex items-center justify-between">
                <span className="w-7" />
                <span className="text-sm font-semibold text-foreground-800">{formatMonthYear(new Date(viewMonth === 11 ? viewYear + 1 : viewYear, viewMonth === 11 ? 0 : viewMonth + 1, 1))}</span>
                <button type="button" onClick={() => moveLeft(1)} aria-label="Next" className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md hover:bg-background-100">
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
              <MonthGrid cells={rightCells} dayProps={dayProps} onSelect={selectDay} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MonthGrid({
  cells,
  dayProps,
  onSelect,
}: {
  cells: (Date | null)[];
  dayProps: (day: Date) => { inRange: boolean; isStart: boolean; isEnd: boolean };
  onSelect: (day: Date) => void;
}) {
  return (
    <>
      <div className="grid grid-cols-7 text-center text-[10px] text-foreground-500">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-0.5">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center">
        {cells.map((day, i) =>
          day ? <DayButton key={i} day={day} {...dayProps(day)} onClick={() => onSelect(day)} /> : <span key={i} />
        )}
      </div>
    </>
  );
}